import React, { useState, useEffect } from "react";
import { lightenColor } from "@/utils/lightenColor";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import { useTranslations } from "next-intl";
import clsx from "clsx";

export const QuantityStepper = ({
  handleQuantityChange,
  quantity,
  disableMinus,
  disablePlus,
  disableInput,
  id,
  modifierId,
  min = 0,
  max,
  perUnitName,
  isModalQuantity = false,
  merchantColor,
  totalQuantity,
  adaName,
  buttonClass = "",
}: any) => {
  const t = useTranslations("QuantityStepper");
  const mathFontStyle: React.CSSProperties = {
    fontFamily: "'Menlo', 'Lucida Console', 'Monaco', 'Consolas', monospace",
  };

  const [inputValue, setInputValue] = useState<string | number>(quantity);

  useEffect(() => {
    setInputValue(quantity);
  }, [quantity]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleBlur = () => {
    const value = parseInt(inputValue as string, 10);

    if (isNaN(value) || value < min) {
      setInputValue(min);
      handleQuantityChange(min);
      return;
    }

    if (totalQuantity !== undefined && !isModalQuantity) {
      const otherItemsQuantity = totalQuantity - quantity;
      if (value + otherItemsQuantity > max) {
        const newValue = Math.max(max - otherItemsQuantity, min);
        const maxNewValue = Math.min(newValue, 99);
        setInputValue(maxNewValue);
        handleQuantityChange(maxNewValue);
      } else {
        const newValue = Math.min(value, 99);
        setInputValue(newValue);
        handleQuantityChange(newValue);
      }
    } else {
      if (value > max) {
        setInputValue(max);
        handleQuantityChange(max);
      } else {
        setInputValue(value);
        handleQuantityChange(value);
      }
    }
  };

  return (
    <div
      className={`flex flex-row w-full`}
      data-testid={
        modifierId
          ? applyDataTestId(pom.steppers.quantityStepper) + modifierId
          : applyDataTestId(pom.steppers.itemQuantityStepper)
      }
    >
      <button
        onClick={() => handleQuantityChange(quantity - 1)}
        className={clsx(
          `flex items-center py-1 ${buttonClass}`,
          isModalQuantity
            ? "pl-3 pb-2 flex-1 justify-center text-[30px] text-[#727272]"
            : "px-3 h-9 border rounded-l-md text-[20px] disabled:text-grayDisabled"
        )}
        style={mathFontStyle}
        disabled={disableMinus || totalQuantity === min || quantity <= min}
        aria-label={adaName ? t("decreaseFor", { adaName }) : t("decrease")}
        aria-disabled={disableMinus || totalQuantity === min || quantity <= min}
        data-testid={applyDataTestId(pom.icons.minusIcon)}
        aria-controls={id}
      >
        -
      </button>
      <div className="flex flex-row items-center">
        <input
          id={id}
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          className={
            isModalQuantity
              ? `text-center text-black`
              : `w-12 h-9 text-center border-t border-b border-gray-300 appearance-none disabled:text-grayDisabled`
          }
          min={min}
          max={max}
          style={
            isModalQuantity
              ? {
                  appearance: "textfield",
                  MozAppearance: "textfield",
                  WebkitAppearance: "none",
                  backgroundColor: lightenColor(merchantColor, 90),
                  width: `${Math.max(inputValue.toString().length, 3)}ch`,
                }
              : {
                  appearance: "textfield",
                  MozAppearance: "textfield",
                  WebkitAppearance: "none",
                }
          }
          inputMode="numeric"
          role="spinbutton"
          disabled={disableInput}
          aria-label={adaName ? `Quantity for ${adaName}` : "Quantity"}
          aria-valuenow={Number(inputValue)}
          aria-valuemin={min}
          aria-valuemax={max}
        />
        {perUnitName && (
          <span className="font-medium text-black">{perUnitName}</span>
        )}
      </div>
      <button
        onClick={() => handleQuantityChange(quantity + 1)}
        className={clsx(
          `flex items-center py-1 ${buttonClass}`,
          isModalQuantity
            ? "pl-3 pb-2 flex-1 justify-center text-[30px] text-[#727272]"
            : "px-3 h-9 border rounded-r-md text-[20px] disabled:text-grayDisabled"
        )}
        style={mathFontStyle}
        disabled={
          disablePlus ||
          (totalQuantity ? totalQuantity >= max : quantity >= max)
        }
        aria-label={adaName ? t("increaseFor", { adaName }) : t("increase")}
        aria-controls={id}
        aria-disabled={
          disablePlus ||
          (totalQuantity ? totalQuantity >= max : quantity >= max)
        }
        data-testid={applyDataTestId(pom.icons.plusIcon)}
      >
        +
      </button>
    </div>
  );
};
